// src/components/HandwritingGenerator.tsx
import { useState as useState2, useEffect as useEffect2 } from "react";

// src/components/ui/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-soft hover:shadow-card",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-gradient-accent text-accent-foreground hover:opacity-90 shadow-accent"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ui/popover.tsx
import * as React2 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx2 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverContent = React2.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx2(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx2(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/ui/textarea.tsx
import * as React3 from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Textarea = React3.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx3(
      "textarea",
      {
        className: cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/components/ui/slider.tsx
import * as React4 from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var Slider = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(
  SliderPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex w-full touch-none select-none items-center",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx4(SliderPrimitive.Track, { className: "relative h-2 w-full grow overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx4(SliderPrimitive.Range, { className: "absolute h-full bg-primary" }) }),
      /* @__PURE__ */ jsx4(SliderPrimitive.Thumb, { className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" })
    ]
  }
));
Slider.displayName = SliderPrimitive.Root.displayName;

// src/components/ui/switch.tsx
import * as React5 from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx5 } from "react/jsx-runtime";
var Switch = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx5(
  SwitchPrimitives.Root,
  {
    className: cn(
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx5(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;

// src/components/ui/label.tsx
import * as React6 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx6 } from "react/jsx-runtime";
var labelVariants = cva2(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;

// src/components/HandwritingGenerator.tsx
import { Upload as Upload2, Download as Download2, Image as Image2, Type, ChevronDown as ChevronDown3, FileText as FileText2, Check as Check4, RotateCcw as RotateCcw2, Lock as Lock2, ALargeSmall, Youtube as Youtube2, BookOpen, Mic as Mic2 } from "lucide-react";

// src/components/TextRecognition.tsx
import { useCallback } from "react";
import { Image, FileText, Youtube, Lock, Mic } from "lucide-react";
import { Fragment, jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
var defaultRecognitionState = {
  tab: "photo",
  photoFiles: [],
  docFile: null,
  youtubeUrl: "",
  audioFile: null,
  isProcessing: false,
  error: ""
};
function TextRecognition({
  state,
  onChange,
  onTextExtracted,
  onFileUpload,
  onDocumentUpload,
  onYoutubeRecognize,
  onAudioRecognize,
  onLocked,
  isLocked = false,
  texts
}) {
  const { tab, photoFiles, docFile, youtubeUrl, audioFile, isProcessing, error } = state;
  const handlePhotoRecognize = useCallback(async () => {
    if (isLocked) {
      onLocked?.();
      return;
    }
    if (!photoFiles.length || !onFileUpload) return;
    const dt = new DataTransfer();
    photoFiles.forEach((f) => dt.items.add(f));
    onChange({ isProcessing: true, error: "" });
    try {
      const text = await onFileUpload(dt.files);
      onChange({ isProcessing: false });
      onTextExtracted(text);
    } catch {
      onChange({ isProcessing: false, error: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0440\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0432\u0430\u043D\u0438\u044F" });
    }
  }, [photoFiles, onFileUpload, onTextExtracted, onChange]);
  const handleDocumentUpload = useCallback(async () => {
    if (isLocked) {
      onLocked?.();
      return;
    }
    if (!docFile || !onDocumentUpload) return;
    onChange({ isProcessing: true, error: "" });
    try {
      const text = await onDocumentUpload(docFile);
      onChange({ isProcessing: false });
      onTextExtracted(text);
    } catch {
      onChange({ isProcessing: false, error: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0440\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0432\u0430\u043D\u0438\u044F \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u0430" });
    }
  }, [docFile, onDocumentUpload, onTextExtracted, onChange]);
  const handleYoutube = useCallback(async () => {
    if (isLocked) {
      onLocked?.();
      return;
    }
    if (!youtubeUrl || !onYoutubeRecognize) return;
    onChange({ isProcessing: true, error: "" });
    try {
      const text = await onYoutubeRecognize(youtubeUrl);
      onChange({ isProcessing: false, youtubeUrl: "" });
      onTextExtracted(text);
    } catch {
      onChange({ isProcessing: false, error: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0440\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0432\u0430\u043D\u0438\u044F \u0432\u0438\u0434\u0435\u043E" });
    }
  }, [youtubeUrl, onYoutubeRecognize, onTextExtracted, onChange]);
  const handleAudio = useCallback(async () => {
    if (isLocked) {
      onLocked?.();
      return;
    }
    if (!audioFile || !onAudioRecognize) return;
    onChange({ isProcessing: true, error: "" });
    try {
      const text = await onAudioRecognize(audioFile);
      onChange({ isProcessing: false, audioFile: null });
      onTextExtracted(text);
    } catch {
      onChange({ isProcessing: false, error: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0440\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0432\u0430\u043D\u0438\u044F \u0430\u0443\u0434\u0438\u043E" });
    }
  }, [audioFile, onAudioRecognize, onTextExtracted, onChange]);
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) onChange({ photoFiles: Array.from(e.dataTransfer.files), error: "" });
  }, [onChange]);
  const tabs = [
    { id: "photo", label: texts.tabPhoto, icon: /* @__PURE__ */ jsx7(Image, { className: "w-3.5 h-3.5" }) },
    { id: "document", label: texts.tabDocument, icon: /* @__PURE__ */ jsx7(FileText, { className: "w-3.5 h-3.5" }) },
    { id: "youtube", label: texts.tabVideo, icon: /* @__PURE__ */ jsx7(Youtube, { className: "w-3.5 h-3.5" }) },
    { id: "audio", label: texts.tabAudio, icon: /* @__PURE__ */ jsx7(Mic, { className: "w-3.5 h-3.5" }) }
  ];
  return /* @__PURE__ */ jsxs2("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsxs2("div", { children: [
      /* @__PURE__ */ jsx7("h3", { className: "text-base font-semibold mb-1", children: texts.title }),
      /* @__PURE__ */ jsxs2("p", { className: "text-xs text-muted-foreground", children: [
        tab === "photo" && texts.photoDesc,
        tab === "document" && texts.documentDesc,
        tab === "youtube" && texts.youtubeDesc,
        tab === "audio" && texts.audioDesc
      ] })
    ] }),
    /* @__PURE__ */ jsx7("div", { className: "flex gap-1 bg-muted rounded-lg p-1", children: tabs.map((t) => /* @__PURE__ */ jsxs2(
      "button",
      {
        onClick: () => !isProcessing && onChange({ tab: t.id, error: "" }),
        disabled: isProcessing && tab !== t.id,
        className: `flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === t.id ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"} ${isProcessing && tab === t.id ? "relative" : ""}`,
        children: [
          isProcessing && tab === t.id && /* @__PURE__ */ jsx7("div", { className: "w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" }),
          (!isProcessing || tab !== t.id) && t.icon,
          t.label
        ]
      },
      t.id
    )) }),
    error && /* @__PURE__ */ jsx7("p", { className: "text-xs text-red-500", children: error }),
    tab === "photo" && /* @__PURE__ */ jsxs2("div", { className: "space-y-3", children: [
      /* @__PURE__ */ jsxs2(
        "label",
        {
          className: `flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-6 transition-colors ${!isProcessing ? "cursor-pointer hover:border-accent/50" : ""}`,
          onDragEnter: handleDrag,
          onDragLeave: handleDrag,
          onDragOver: handleDrag,
          onDrop: handleDrop,
          children: [
            /* @__PURE__ */ jsx7(Image, { className: "w-8 h-8 text-muted-foreground mb-2" }),
            /* @__PURE__ */ jsx7("span", { className: "text-sm text-muted-foreground", children: texts.photoHint }),
            photoFiles.length > 0 && /* @__PURE__ */ jsxs2("span", { className: "text-xs text-accent mt-1 font-medium", children: [
              photoFiles.length,
              " \u0444\u043E\u0442\u043E \u0432\u044B\u0431\u0440\u0430\u043D\u043E"
            ] }),
            !isProcessing && /* @__PURE__ */ jsx7(
              "input",
              {
                type: "file",
                multiple: true,
                accept: "image/*",
                className: "hidden",
                onChange: (e) => onChange({ photoFiles: Array.from(e.target.files ?? []), error: "" })
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx7(
        Button,
        {
          onClick: handlePhotoRecognize,
          disabled: !photoFiles.length && !isLocked || isProcessing || !onFileUpload,
          size: "sm",
          className: "w-full bg-gradient-accent hover:bg-accent shadow-accent",
          children: isProcessing ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7("div", { className: "w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" }),
            texts.recognizing
          ] }) : isLocked ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Lock, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizePhoto
          ] }) : /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Image, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizePhoto
          ] })
        }
      )
    ] }),
    tab === "document" && /* @__PURE__ */ jsxs2("div", { className: "space-y-3", children: [
      /* @__PURE__ */ jsxs2("label", { className: `flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-6 transition-colors ${!isProcessing ? "cursor-pointer hover:border-accent/50" : ""}`, children: [
        /* @__PURE__ */ jsx7(FileText, { className: "w-8 h-8 text-muted-foreground mb-2" }),
        /* @__PURE__ */ jsx7("span", { className: "text-sm text-muted-foreground", children: texts.documentHint }),
        docFile && /* @__PURE__ */ jsx7("span", { className: "text-xs text-accent mt-1 font-medium", children: docFile.name }),
        !isProcessing && /* @__PURE__ */ jsx7(
          "input",
          {
            type: "file",
            accept: ".pdf,.docx",
            className: "hidden",
            onChange: (e) => onChange({ docFile: e.target.files?.[0] ?? null })
          }
        )
      ] }),
      /* @__PURE__ */ jsx7(
        Button,
        {
          onClick: handleDocumentUpload,
          disabled: !docFile && !isLocked || isProcessing || !onDocumentUpload,
          size: "sm",
          className: "w-full bg-gradient-accent hover:bg-accent shadow-accent",
          children: isProcessing ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7("div", { className: "w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" }),
            texts.recognizing
          ] }) : isLocked ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Lock, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizeDocument
          ] }) : /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(FileText, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizeDocument
          ] })
        }
      )
    ] }),
    tab === "youtube" && /* @__PURE__ */ jsxs2("div", { className: "space-y-3", children: [
      /* @__PURE__ */ jsx7("p", { className: "text-xs text-muted-foreground", children: texts.youtubeHint }),
      /* @__PURE__ */ jsx7(
        "input",
        {
          type: "url",
          className: "w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50",
          placeholder: "https://youtube.com/watch?v=...",
          value: youtubeUrl,
          disabled: isProcessing,
          onChange: (e) => onChange({ youtubeUrl: e.target.value })
        }
      ),
      isProcessing && /* @__PURE__ */ jsx7("p", { className: "text-xs text-muted-foreground", children: texts.youtubeHintProcessing }),
      /* @__PURE__ */ jsx7(
        Button,
        {
          onClick: handleYoutube,
          disabled: !youtubeUrl && !isLocked || isProcessing || !onYoutubeRecognize,
          size: "sm",
          className: "w-full bg-gradient-accent hover:bg-accent shadow-accent",
          children: isProcessing ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7("div", { className: "w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" }),
            texts.recognizing
          ] }) : isLocked ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Lock, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizeVideo
          ] }) : /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Youtube, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizeVideo
          ] })
        }
      )
    ] }),
    tab === "audio" && /* @__PURE__ */ jsxs2("div", { className: "space-y-3", children: [
      /* @__PURE__ */ jsxs2("label", { className: `flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-6 transition-colors ${!isProcessing ? "cursor-pointer hover:border-accent/50" : ""}`, children: [
        /* @__PURE__ */ jsx7(Mic, { className: "w-8 h-8 text-muted-foreground mb-2" }),
        /* @__PURE__ */ jsx7("span", { className: "text-sm text-muted-foreground", children: texts.audioHint }),
        audioFile && /* @__PURE__ */ jsx7("span", { className: "text-xs text-accent mt-1 font-medium", children: audioFile.name }),
        !isProcessing && /* @__PURE__ */ jsx7(
          "input",
          {
            type: "file",
            accept: ".mp3,.m4a,.ogg,.wav,.webm,.flac,.mp4",
            className: "hidden",
            onChange: (e) => onChange({ audioFile: e.target.files?.[0] ?? null })
          }
        )
      ] }),
      /* @__PURE__ */ jsx7(
        Button,
        {
          onClick: handleAudio,
          disabled: !audioFile && !isLocked || isProcessing || !onAudioRecognize,
          size: "sm",
          className: "w-full bg-gradient-accent hover:bg-accent shadow-accent",
          children: isProcessing ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7("div", { className: "w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" }),
            texts.recognizing
          ] }) : isLocked ? /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Lock, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizeAudio
          ] }) : /* @__PURE__ */ jsxs2(Fragment, { children: [
            /* @__PURE__ */ jsx7(Mic, { className: "w-3.5 h-3.5 mr-1.5" }),
            texts.recognizeAudio
          ] })
        }
      )
    ] })
  ] });
}

// src/components/PreviewArea.tsx
import { Download } from "lucide-react";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
function PreviewArea({ text, textConfig, backgroundConfig, PreviewComponent, texts }) {
  return /* @__PURE__ */ jsx8("div", { className: "space-y-4", children: /* @__PURE__ */ jsxs3(
    "div",
    {
      className: "flex justify-center p-4 lg:p-8 relative bg-white rounded-lg shadow-soft overflow-hidden",
      children: [
        !text?.length && /* @__PURE__ */ jsx8("div", { className: "inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm min-h-[300px] lg:min-h-[calc(100vh-8rem-109px)]", children: /* @__PURE__ */ jsxs3("div", { className: "text-center animate-fade-in px-4", children: [
          /* @__PURE__ */ jsx8("div", { className: "w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-4 bg-gradient-primary rounded-full flex items-center justify-center animate-float", children: /* @__PURE__ */ jsx8(Download, { className: "w-6 h-6 lg:w-8 lg:h-8 text-white" }) }),
          /* @__PURE__ */ jsx8("p", { className: "text-muted-foreground text-sm lg:text-base", children: texts.placeholder })
        ] }) }),
        text && PreviewComponent && /* @__PURE__ */ jsx8(PreviewComponent, { text })
      ]
    }
  ) });
}

// src/components/AiChatInterface.tsx
import React11, { useState, useRef, useEffect } from "react";

// src/components/ui/select.tsx
import * as React8 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React8.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx9(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx9(ChevronDown, { className: "h-4 w-4 opacity-50" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx9(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx9(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React8.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx9(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs4(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx9(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx9(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx9(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React8.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx9("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx9(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx9(Check, { className: "h-4 w-4" }) }) }),
      /* @__PURE__ */ jsx9(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/ui/dropdown-menu.tsx
import * as React9 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check as Check2, ChevronRight, Circle } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuSubTrigger = React9.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs5(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx10(ChevronRight, { className: "ml-auto h-4 w-4" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = React9.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx10(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx10(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React9.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx10(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = React9.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs5(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx10("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx10(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx10(Check2, { className: "h-4 w-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = React9.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs5(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx10("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx10(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx10(Circle, { className: "h-2 w-2 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = React9.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx10(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx10(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// src/components/ui/dialog.tsx
import * as React10 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogPortal = DialogPrimitive.Portal;
var DialogOverlay = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-[70] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs6(DialogPortal, { children: [
  /* @__PURE__ */ jsx11(DialogOverlay, {}),
  /* @__PURE__ */ jsxs6(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-[70] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs6(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", children: [
          /* @__PURE__ */ jsx11(X, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx11("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx11(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx11(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/AiChatInterface.tsx
import { Send, Loader2, Copy, Check as Check3, ChevronDown as ChevronDown2, ChevronUp as ChevronUp2, Maximize2, Plus, Pencil, Trash2, MessageSquare, X as X2 } from "lucide-react";
import { Fragment as Fragment2, jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
function AiChatInterface({
  messages,
  isProcessing,
  availableTokens,
  aiActions,
  onSendMessage,
  className = "",
  texts,
  isFullscreen: isFullscreenProp,
  onSetFullscreen,
  chats = [],
  currentChatId,
  onSelectChat,
  onCreateChat,
  onRenameChat,
  onDeleteChat,
  hideFullscreen = false,
  hideHeader = false,
  alwaysExpand = false,
  inputDisabled = false,
  largeInput = false,
  streamingMessageId = null,
  isCreatingChat = false,
  renamingLoadingId = null,
  deletingChatId = null
}) {
  const [inputText, setInputText] = useState("");
  const [selectedAction, setSelectedAction] = useState(aiActions[0]?.id || "");
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState(/* @__PURE__ */ new Set());
  const [isFullscreenInternal, setIsFullscreenInternal] = useState(false);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const isFullscreen = isFullscreenProp ?? isFullscreenInternal;
  const setIsFullscreen = (v) => {
    onSetFullscreen ? onSetFullscreen(v) : setIsFullscreenInternal(v);
  };
  const messagesEndRef = useRef(null);
  const fullscreenMessagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const fullscreenScrollContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const prevMessageCountRef = useRef(0);
  const isNearBottom = (container) => container.scrollHeight - container.scrollTop - container.clientHeight < 80;
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let prevScrollTop = container.scrollTop;
    const onScroll = () => {
      const current = container.scrollTop;
      if (current < prevScrollTop) {
        userScrolledUpRef.current = true;
      } else if (isNearBottom(container)) {
        userScrolledUpRef.current = false;
      }
      prevScrollTop = current;
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, []);
  const scrollToBottom = (force = false) => {
    const behavior = force ? "smooth" : "instant";
    if (scrollContainerRef.current && (force || !userScrolledUpRef.current)) {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
    if (fullscreenScrollContainerRef.current && (force || !userScrolledUpRef.current)) {
      fullscreenMessagesEndRef.current?.scrollIntoView({ behavior });
    }
  };
  useEffect(() => {
    const newMessageAdded = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (newMessageAdded) {
      userScrolledUpRef.current = false;
      scrollToBottom(true);
    } else {
      scrollToBottom(false);
    }
  }, [messages, isProcessing]);
  useEffect(() => {
    if (isFullscreen) {
      setTimeout(() => fullscreenMessagesEndRef.current?.scrollIntoView({ behavior: "instant" }), 50);
    }
  }, [isFullscreen]);
  const handleSend = () => {
    if (inputText.trim() && selectedAction && !isProcessing && !inputDisabled) {
      onSendMessage(inputText.trim(), selectedAction);
      setInputText("");
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2e3);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };
  const toggleMessageExpansion = (messageId) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };
  const isMessageLong = (text) => text.length > 200;
  const getTruncatedText = (text) => text.substring(0, 200) + "...";
  const shouldShowText = (message) => {
    return !isMessageLong(message.text) || expandedMessages.has(message.id);
  };
  const selectedActionData = aiActions.find((a) => a.id === selectedAction);
  const renderMessages = (endRef, containerRef, expandAll = false) => /* @__PURE__ */ jsxs7("div", { ref: containerRef, className: cn("flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth", { "flex items-center justify-center": messages.length === 0 }), children: [
    messages.length === 0 && !isProcessing ? /* @__PURE__ */ jsxs7("div", { className: "text-center text-gray-500 py-8", children: [
      /* @__PURE__ */ jsx12("div", { className: "mb-2", children: "\u{1F4AC}" }),
      /* @__PURE__ */ jsx12("p", { className: "text-sm", children: texts.emptyState.split("\n").map((line, i) => /* @__PURE__ */ jsxs7(React11.Fragment, { children: [
        line,
        i < texts.emptyState.split("\n").length - 1 && /* @__PURE__ */ jsx12("br", {})
      ] }, i)) })
    ] }) : messages.map((message) => /* @__PURE__ */ jsx12(
      "div",
      {
        className: `flex ${message.isUser ? "justify-end" : "justify-start"}`,
        children: /* @__PURE__ */ jsxs7(
          "div",
          {
            className: `max-w-[80%] rounded-lg p-3 relative group ${message.isUser ? "bg-gradient-accent text-white" : "bg-gray-100 text-gray-900"}`,
            children: [
              /* @__PURE__ */ jsx12(
                "button",
                {
                  onClick: () => copyToClipboard(message.text, message.id),
                  className: "absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-white hover:bg-gray-50 text-gray-600 shadow-sm border",
                  title: texts.copy,
                  children: copiedMessageId === message.id ? /* @__PURE__ */ jsx12(Check3, { className: "w-3 h-3" }) : /* @__PURE__ */ jsx12(Copy, { className: "w-3 h-3" })
                }
              ),
              message.action && message.isUser && /* @__PURE__ */ jsx12("div", { className: "text-xs opacity-75 mb-1 font-semibold", children: texts.getAction(aiActions.find((a) => a.id === message.action)?.name ?? "") }),
              /* @__PURE__ */ jsxs7("div", { className: "whitespace-pre-wrap text-sm pr-2", children: [
                expandAll || alwaysExpand || shouldShowText(message) ? message.text : getTruncatedText(message.text),
                streamingMessageId === message.id && /* @__PURE__ */ jsx12("span", { className: "inline-block ml-1.5 align-middle", style: {
                  animation: "pencil-write 0.6s ease-in-out infinite alternate",
                  transformOrigin: "bottom left"
                }, children: /* @__PURE__ */ jsx12(Pencil, { size: 13, className: "opacity-60" }) })
              ] }),
              !expandAll && !alwaysExpand && isMessageLong(message.text) && /* @__PURE__ */ jsx12(
                "button",
                {
                  onClick: () => toggleMessageExpansion(message.id),
                  className: "text-xs text-blue-500 hover:text-blue-700 mt-1 flex items-center gap-1",
                  children: expandedMessages.has(message.id) ? /* @__PURE__ */ jsxs7(Fragment2, { children: [
                    texts.collapse,
                    " ",
                    /* @__PURE__ */ jsx12(ChevronUp2, { className: "w-3 h-3" })
                  ] }) : /* @__PURE__ */ jsxs7(Fragment2, { children: [
                    texts.expand,
                    " ",
                    /* @__PURE__ */ jsx12(ChevronDown2, { className: "w-3 h-3" })
                  ] })
                }
              ),
              /* @__PURE__ */ jsx12("div", { className: "text-xs opacity-75 mt-1 font-semibold", children: message.timestamp.toLocaleString(texts.locale, {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              }) })
            ]
          }
        )
      },
      message.id
    )),
    isProcessing && /* @__PURE__ */ jsx12("div", { className: "flex justify-start", children: /* @__PURE__ */ jsxs7("div", { className: "bg-gray-100 rounded-lg p-3 flex items-center gap-2", children: [
      /* @__PURE__ */ jsx12(Loader2, { className: "w-4 h-4 animate-spin" }),
      /* @__PURE__ */ jsx12("span", { className: "text-sm text-gray-600", children: texts.thinking })
    ] }) }),
    /* @__PURE__ */ jsx12("div", { className: "!m-0", ref: endRef })
  ] });
  const renderInput = (tall = false) => /* @__PURE__ */ jsxs7("div", { className: "p-4 border-t border-border space-y-2 flex-shrink-0", children: [
    /* @__PURE__ */ jsxs7(Select, { value: selectedAction, onValueChange: setSelectedAction, children: [
      /* @__PURE__ */ jsx12(SelectTrigger, { className: "w-full", children: /* @__PURE__ */ jsx12(SelectValue, { placeholder: texts.actionPlaceholder }) }),
      /* @__PURE__ */ jsx12(SelectContent, { children: aiActions.map((action) => /* @__PURE__ */ jsx12(SelectItem, { value: action.id, children: action.name }, action.id)) })
    ] }),
    /* @__PURE__ */ jsxs7("div", { className: "flex gap-2 relative", children: [
      /* @__PURE__ */ jsx12(
        Textarea,
        {
          placeholder: texts.inputPlaceholder,
          value: inputText,
          onChange: (e) => setInputText(e.target.value),
          onKeyPress: handleKeyPress,
          className: `flex-1 resize-none pr-[40px] ${tall || largeInput ? "h-[150px]" : "min-h-[60px] max-h-[120px]"}`,
          disabled: isProcessing || inputDisabled
        }
      ),
      /* @__PURE__ */ jsx12(
        Button,
        {
          onClick: handleSend,
          disabled: !inputText.trim() || !selectedAction || isProcessing || inputDisabled,
          size: "sm",
          className: "self-end bg-gradient-accent hover:bg-accent !rounded-full absolute top-0 right-2 bottom-0 w-[40px] h-[40px] m-auto",
          children: isProcessing ? /* @__PURE__ */ jsx12(Loader2, { className: "w-4 h-4 animate-spin" }) : /* @__PURE__ */ jsx12(Send, { className: "w-4 h-4" })
        }
      )
    ] }),
    selectedActionData?.description && /* @__PURE__ */ jsx12("div", { className: "text-xs text-muted-foreground", children: selectedActionData.description })
  ] });
  const currentChat = chats.find((c) => c.id === currentChatId);
  const renderHeader = (showExpand = true) => /* @__PURE__ */ jsxs7("div", { className: "px-4 pt-4 pb-3 border-b border-border flex-shrink-0", children: [
    /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-between gap-2", children: [
      chats.length > 0 ? /* @__PURE__ */ jsxs7(DropdownMenu, { children: [
        /* @__PURE__ */ jsx12(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs7("button", { className: "flex items-center gap-1.5 text-sm font-medium hover:text-foreground/80 transition-colors min-w-0 flex-1 truncate text-left", children: [
          /* @__PURE__ */ jsx12(MessageSquare, { className: "w-3.5 h-3.5 shrink-0 text-muted-foreground" }),
          /* @__PURE__ */ jsx12("span", { className: "truncate", children: currentChat?.title || texts.newChat }),
          /* @__PURE__ */ jsx12(ChevronDown2, { className: "w-3.5 h-3.5 shrink-0 text-muted-foreground" })
        ] }) }),
        /* @__PURE__ */ jsxs7(DropdownMenuContent, { align: "start", className: "w-64 max-h-72 overflow-y-auto p-2", children: [
          /* @__PURE__ */ jsxs7(
            "button",
            {
              onClick: (e) => {
                e.stopPropagation();
                onCreateChat?.();
              },
              disabled: isCreatingChat,
              className: "w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-accent text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-70 mb-2",
              children: [
                isCreatingChat ? /* @__PURE__ */ jsx12(Loader2, { className: "w-4 h-4 animate-spin" }) : /* @__PURE__ */ jsx12(Plus, { className: "w-4 h-4" }),
                texts.createNewChat
              ]
            }
          ),
          /* @__PURE__ */ jsx12("div", { className: "border-b border-border mb-2" }),
          chats.map((chat) => /* @__PURE__ */ jsx12(
            "div",
            {
              className: cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer mb-1 transition-colors",
                chat.id === currentChatId ? "bg-white border border-border shadow-sm" : "",
                deletingChatId === chat.id ? "opacity-50 pointer-events-none" : ""
              ),
              onClick: () => chat.id !== currentChatId && onSelectChat?.(chat.id),
              children: renamingChatId === chat.id ? /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-1 flex-1 min-w-0", onClick: (e) => e.stopPropagation(), children: [
                /* @__PURE__ */ jsx12(
                  "input",
                  {
                    autoFocus: true,
                    className: "flex-1 text-sm border rounded px-1.5 py-0.5 min-w-0 bg-white",
                    value: renameValue,
                    onChange: (e) => setRenameValue(e.target.value),
                    onKeyDown: (e) => {
                      if (e.key === "Enter") {
                        onRenameChat?.(chat.id, renameValue);
                        setRenamingChatId(null);
                      }
                      if (e.key === "Escape") setRenamingChatId(null);
                      e.stopPropagation();
                    }
                  }
                ),
                /* @__PURE__ */ jsx12("button", { disabled: renamingLoadingId === chat.id, className: "text-green-600 hover:text-green-700 shrink-0 disabled:opacity-50", onClick: () => {
                  onRenameChat?.(chat.id, renameValue);
                  setRenamingChatId(null);
                }, children: renamingLoadingId === chat.id ? /* @__PURE__ */ jsx12(Loader2, { className: "w-3.5 h-3.5 animate-spin" }) : /* @__PURE__ */ jsx12(Check3, { className: "w-3.5 h-3.5" }) }),
                /* @__PURE__ */ jsx12("button", { disabled: renamingLoadingId === chat.id, className: "text-muted-foreground shrink-0 disabled:opacity-50", onClick: () => setRenamingChatId(null), children: /* @__PURE__ */ jsx12(X2, { className: "w-3.5 h-3.5" }) })
              ] }) : /* @__PURE__ */ jsxs7(Fragment2, { children: [
                /* @__PURE__ */ jsx12(MessageSquare, { className: "w-3.5 h-3.5 text-muted-foreground shrink-0" }),
                /* @__PURE__ */ jsx12("span", { className: "truncate flex-1 text-sm", children: chat.title || texts.newChat }),
                chat.id === currentChatId && /* @__PURE__ */ jsxs7("div", { className: "flex gap-0.5 shrink-0", children: [
                  /* @__PURE__ */ jsx12(
                    "button",
                    {
                      className: "p-1 rounded text-muted-foreground hover:text-foreground",
                      onClick: (e) => {
                        e.stopPropagation();
                        setRenamingChatId(chat.id);
                        setRenameValue(chat.title);
                      },
                      children: /* @__PURE__ */ jsx12(Pencil, { className: "w-3 h-3" })
                    }
                  ),
                  /* @__PURE__ */ jsx12(
                    "button",
                    {
                      disabled: deletingChatId === chat.id,
                      className: "p-1 rounded text-muted-foreground hover:text-red-500 disabled:opacity-50",
                      onClick: (e) => {
                        e.stopPropagation();
                        onDeleteChat?.(chat.id);
                      },
                      children: deletingChatId === chat.id ? /* @__PURE__ */ jsx12(Loader2, { className: "w-3 h-3 animate-spin" }) : /* @__PURE__ */ jsx12(Trash2, { className: "w-3 h-3" })
                    }
                  )
                ] })
              ] })
            },
            chat.id
          ))
        ] })
      ] }) : /* @__PURE__ */ jsx12("h4", { className: "font-medium flex-1", children: texts.title }),
      /* @__PURE__ */ jsx12("div", { className: "flex items-center gap-1 shrink-0", children: showExpand && !hideFullscreen && /* @__PURE__ */ jsx12("button", { onClick: () => setIsFullscreen(true), className: "text-muted-foreground hover:text-foreground transition-colors", title: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043D\u0430 \u0432\u0435\u0441\u044C \u044D\u043A\u0440\u0430\u043D", children: /* @__PURE__ */ jsx12(Maximize2, { className: "w-4 h-4" }) }) })
    ] }),
    availableTokens !== void 0 && /* @__PURE__ */ jsx12("span", { className: "text-xs text-muted-foreground mt-0.5 block", children: texts.getTokensAvailable(availableTokens) })
  ] });
  return /* @__PURE__ */ jsxs7(Fragment2, { children: [
    /* @__PURE__ */ jsxs7("div", { className: `flex flex-col h-full ${className}`, children: [
      !hideHeader && renderHeader(),
      renderMessages(messagesEndRef, scrollContainerRef),
      renderInput()
    ] }),
    /* @__PURE__ */ jsx12(Dialog, { open: isFullscreen, onOpenChange: setIsFullscreen, children: /* @__PURE__ */ jsxs7(DialogContent, { className: "max-w-4xl w-[90vw] h-[90vh] flex flex-col p-0 gap-0", children: [
      renderHeader(false),
      renderMessages(fullscreenMessagesEndRef, fullscreenScrollContainerRef, true),
      renderInput(true)
    ] }) })
  ] });
}

// src/components/HandwritingGenerator.tsx
import { Fragment as Fragment3, jsx as jsx13, jsxs as jsxs8 } from "react/jsx-runtime";
function SourceIcon({ meta }) {
  if (!meta?.type) return /* @__PURE__ */ jsx13(FileText2, { className: "w-3.5 h-3.5 text-muted-foreground flex-shrink-0" });
  const icons = {
    photo: /* @__PURE__ */ jsx13(Image2, { className: "w-3.5 h-3.5 text-blue-500 flex-shrink-0" }),
    document: /* @__PURE__ */ jsx13(FileText2, { className: "w-3.5 h-3.5 text-orange-500 flex-shrink-0" }),
    youtube: /* @__PURE__ */ jsx13(Youtube2, { className: "w-3.5 h-3.5 text-red-500 flex-shrink-0" }),
    audio: /* @__PURE__ */ jsx13(Mic2, { className: "w-3.5 h-3.5 text-purple-500 flex-shrink-0" })
  };
  return /* @__PURE__ */ jsx13(Fragment3, { children: icons[meta.type] ?? /* @__PURE__ */ jsx13(FileText2, { className: "w-3.5 h-3.5 text-muted-foreground flex-shrink-0" }) });
}
var defaultTextConfig = {
  font: "Abram",
  fontSize: 31,
  lineHeight: 35,
  letterSpacing: 0,
  wordSpacing: 0,
  rightMargin: 0,
  leftMargin: 8,
  topMargin: 5,
  indentSize: 75,
  color: "black",
  randomSize: false,
  randomSlant: false,
  randomSpacing: false
};
var defaultBackgroundConfig = {
  type: "default",
  paperSize: "A4",
  backgroundType: "lines",
  doublePage: false,
  image: null
};
var defaultTextSettingsLimits = {
  fontSize: { min: 12, max: 60, step: 1 },
  lineHeight: { min: 20, max: 80, step: 1 },
  letterSpacing: { min: -5, max: 20, step: 1 },
  wordSpacing: { min: -10, max: 30, step: 1 },
  leftMargin: { min: 0, max: 100, step: 1 },
  rightMargin: { min: 0, max: 100, step: 1 },
  topMargin: { min: 0, max: 50, step: 1 },
  indentSize: { min: 0, max: 150, step: 5 }
};
var defaultAvailableFonts = [
  "Abram",
  "Anselmo",
  "Benvolio",
  "Capuletty",
  "Djiovanno",
  "Eskal",
  "Gregory",
  "Lorenco",
  "Merkucio",
  "Montekky"
];
function HandwritingGenerator({
  initialTextConfig,
  initialBackgroundConfig,
  initialText = "",
  onTextConfigChange,
  onBackgroundConfigChange,
  onTextChange,
  PreviewComponent,
  textSettingsLimits,
  availableFonts,
  onCustomFontLoad,
  onResetTextSettings,
  onResetBackgroundSettings,
  customFontStorageKey = "customFontData",
  onCustomBackgroundLoad,
  customBackgroundStorageKey = "customBackgroundData",
  onDownloadNormal,
  onDownloadHighRes,
  onDownloadPDF,
  isAuthenticated = false,
  hasSubscription = false,
  hasDoublePage = false,
  hasA4Format = false,
  onShowAuthModal,
  onShowSubscriptionModal,
  onTextRecognition,
  onDocumentRecognition,
  onYoutubeRecognition,
  onAudioRecognition,
  materials = [],
  onLoadMaterial,
  recognitionResetKey,
  leftBanner,
  rightBanner,
  topBanner,
  showNewFeatures = false,
  aiActions = [],
  onAIProcess,
  aiTokens = 0,
  showAIFeatures = false,
  isAIProcessing = false,
  isAIStreaming = false,
  aiStreamingMessageId = null,
  chatMessages = [],
  onSendChatMessage,
  chats = [],
  currentChatId,
  onSelectChat,
  onCreateChat,
  onRenameChat,
  onDeleteChat,
  isCreatingChat,
  renamingLoadingId,
  deletingChatId,
  initialRecognitionTab = null,
  texts
}) {
  const [textConfig, setTextConfig] = useState2({
    ...defaultTextConfig,
    ...initialTextConfig
  });
  const [backgroundConfig, setBackgroundConfig] = useState2({
    ...defaultBackgroundConfig,
    ...initialBackgroundConfig
  });
  const [inputText, setInputText] = useState2(initialText);
  const [isCustomBackgroundLoaded, setIsCustomBackgroundLoaded] = useState2(false);
  const [showRecognitionModal, setShowRecognitionModal] = useState2(false);
  const [recognitionState, setRecognitionState] = useState2(
    initialRecognitionTab ? { ...defaultRecognitionState, tab: initialRecognitionTab, isProcessing: true } : defaultRecognitionState
  );
  const [isAiChatFullscreen, setIsAiChatFullscreen] = useState2(false);
  const [selectedAIAction, setSelectedAIAction] = useState2(aiActions[0]?.id || "");
  const [activeTab, setActiveTab] = useState2("text");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState2(false);
  const limits = {
    ...defaultTextSettingsLimits,
    ...textSettingsLimits
  };
  const fonts = availableFonts || defaultAvailableFonts;
  const handlePDFClick = () => {
    if (!isAuthenticated) {
      onShowAuthModal?.();
      return;
    }
    if (!hasSubscription) {
      setIsAiChatFullscreen(false);
      onShowSubscriptionModal?.("premium");
      return;
    }
    onDownloadPDF?.();
  };
  const handleDoublePageChange = (checked) => {
    if (checked) {
      if (!isAuthenticated) {
        onShowAuthModal?.();
        return;
      }
      if (!hasDoublePage) {
        setIsAiChatFullscreen(false);
        onShowSubscriptionModal?.("premium");
        return;
      }
    }
    handleBackgroundConfigChange({ doublePage: checked });
  };
  const handleA4Click = () => {
    if (!isAuthenticated) {
      onShowAuthModal?.();
      return;
    }
    if (!hasA4Format) {
      setIsAiChatFullscreen(false);
      onShowSubscriptionModal?.("premium");
      return;
    }
    handleBackgroundConfigChange({ paperSize: "A4" });
  };
  const getFontDisplayName = (fontName) => {
    return fontName === "customFont" ? texts.customFontName : fontName;
  };
  const saveCustomFontToStorage = (fontData) => {
    try {
      localStorage.setItem(customFontStorageKey, fontData);
    } catch (error) {
      console.error("Failed to save custom font to localStorage:", error);
    }
  };
  const loadCustomFontFromStorage = () => {
    try {
      return localStorage.getItem(customFontStorageKey);
    } catch (error) {
      console.error("Failed to load custom font from localStorage:", error);
      return null;
    }
  };
  const loadFontFromData = async (fontData) => {
    try {
      const style = document.createElement("style");
      style.innerHTML = `
        @font-face {
          font-family: customFont;
          src: url('${fontData}')
        }
      `;
      document.head.append(style);
      if (document.fonts) {
        await document.fonts.load("10px customFont");
        console.log("Custom font loaded from storage");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load custom font:", error);
      return false;
    }
  };
  const saveCustomBackgroundToStorage = (backgroundData) => {
    try {
      localStorage.setItem(customBackgroundStorageKey, backgroundData);
    } catch (error) {
      console.error("Failed to save custom background to localStorage:", error);
    }
  };
  const loadCustomBackgroundFromStorage = () => {
    try {
      return localStorage.getItem(customBackgroundStorageKey);
    } catch (error) {
      console.error("Failed to load custom background from localStorage:", error);
      return null;
    }
  };
  const loadBackgroundFromData = (backgroundData) => {
    try {
      if (backgroundData.startsWith("data:image/")) {
        console.log("Custom background loaded from storage");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load custom background:", error);
      return false;
    }
  };
  useEffect2(() => {
    if (recognitionResetKey && recognitionResetKey > 0) {
      setRecognitionState(defaultRecognitionState);
    }
  }, [recognitionResetKey]);
  useEffect2(() => {
    if (initialTextConfig) {
      setTextConfig((prev) => ({ ...prev, ...initialTextConfig }));
    }
  }, [initialTextConfig]);
  useEffect2(() => {
    if (initialBackgroundConfig) {
      setBackgroundConfig((prev) => ({ ...prev, ...initialBackgroundConfig }));
    }
  }, [initialBackgroundConfig]);
  useEffect2(() => {
    if (initialText !== void 0) {
      setInputText(initialText);
    }
  }, [initialText]);
  useEffect2(() => {
    const restoreCustomFont = async () => {
      const savedFontData = loadCustomFontFromStorage();
      if (savedFontData) {
        const loaded = await loadFontFromData(savedFontData);
        if (loaded) {
          onCustomFontLoad?.("customFont");
        }
      }
    };
    restoreCustomFont();
  }, []);
  useEffect2(() => {
    const restoreCustomBackground = () => {
      const savedBackgroundData = loadCustomBackgroundFromStorage();
      if (savedBackgroundData) {
        const loaded = loadBackgroundFromData(savedBackgroundData);
        if (loaded) {
          setIsCustomBackgroundLoaded(true);
          onCustomBackgroundLoad?.("customBackground");
        }
      }
    };
    restoreCustomBackground();
  }, []);
  const handleTextConfigChange = (newConfig) => {
    const updatedConfig = { ...textConfig, ...newConfig };
    setTextConfig(updatedConfig);
    onTextConfigChange?.(updatedConfig);
  };
  const handleBackgroundConfigChange = (newConfig) => {
    const updatedConfig = { ...backgroundConfig, ...newConfig };
    setBackgroundConfig(updatedConfig);
    onBackgroundConfigChange?.(updatedConfig);
  };
  const handleTextChange = (newText) => {
    setInputText(newText);
    onTextChange?.(newText);
  };
  const handleDownload = () => {
    console.log("Downloading handwritten note...");
  };
  const handleAIProcess = () => {
    if (!selectedAIAction || !inputText.trim() || !onAIProcess) return;
    onAIProcess(selectedAIAction, inputText);
  };
  useEffect2(() => {
    if (aiActions.length > 0 && !selectedAIAction) {
      setSelectedAIAction(aiActions[0].id);
    }
  }, [aiActions, selectedAIAction]);
  const BACKGROUND_PREVIEWS = {
    A4: {
      grid: "/api/placeholder/120/80?text=\u041A\u043B\u0435\u0442\u043A\u0430+A4",
      lines: "/api/placeholder/120/80?text=\u041B\u0438\u043D\u0438\u0438+A4",
      blank: "/api/placeholder/120/80?text=\u0427\u0438\u0441\u0442\u044B\u0439+A4"
    },
    A5: {
      grid: "/api/placeholder/120/80?text=\u041A\u043B\u0435\u0442\u043A\u0430+A5",
      lines: "/api/placeholder/120/80?text=\u041B\u0438\u043D\u0438\u0438+A5",
      blank: "/api/placeholder/120/80?text=\u0427\u0438\u0441\u0442\u044B\u0439+A5"
    }
  };
  return /* @__PURE__ */ jsxs8("div", { className: "bg-background", children: [
    /* @__PURE__ */ jsx13("div", { className: "sticky top-[60px] z-50 bg-white border-b border-border shadow-sm", children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between gap-2 px-2 lg:px-4 py-2 overflow-x-auto", children: [
      /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsxs8(Popover, { children: [
          /* @__PURE__ */ jsx13(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs8(Button, { variant: "outline", size: "sm", className: "h-10 lg:h-8 gap-1 px-3 lg:px-2", children: [
            /* @__PURE__ */ jsx13(Type, { className: "w-5 h-5 lg:w-4 lg:h-4" }),
            /* @__PURE__ */ jsx13("span", { className: "hidden lg:inline", children: texts.textSettings }),
            /* @__PURE__ */ jsx13(ChevronDown3, { className: "w-4 h-4 lg:w-3 lg:h-3" })
          ] }) }),
          /* @__PURE__ */ jsx13(PopoverContent, { className: "w-[90vw] max-w-md lg:w-72 z-50 bg-white", children: /* @__PURE__ */ jsxs8("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx13("h4", { className: "font-medium", children: texts.textSettings }),
              /* @__PURE__ */ jsxs8(
                Button,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onResetTextSettings,
                  className: "h-6 text-xs text-destructive hover:text-accent-foreground",
                  disabled: !onResetTextSettings,
                  children: [
                    /* @__PURE__ */ jsx13(RotateCcw2, { className: "w-3 h-3" }),
                    texts.reset
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs8("div", { className: "space-y-3", children: [
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium", children: texts.font }),
                /* @__PURE__ */ jsx13(
                  "select",
                  {
                    value: textConfig.font,
                    onChange: (e) => handleTextConfigChange({ font: e.target.value }),
                    className: "w-full h-10 lg:h-8 px-2 border border-border rounded text-sm lg:text-sm text-base",
                    children: fonts.map((font) => /* @__PURE__ */ jsx13("option", { value: font, children: getFontDisplayName(font) }, font))
                  }
                ),
                /* @__PURE__ */ jsx13("div", { className: "text-center", children: /* @__PURE__ */ jsx13("span", { className: "text-xs text-muted-foreground", children: texts.or }) }),
                /* @__PURE__ */ jsxs8(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    className: "w-full h-10 lg:h-8 mt-1",
                    onClick: () => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".ttf,.otf,.woff,.woff2";
                      input.onchange = async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = async () => {
                            const fontData = reader.result;
                            const loaded = await loadFontFromData(fontData);
                            if (loaded) {
                              saveCustomFontToStorage(fontData);
                              onCustomFontLoad?.("customFont");
                              handleTextConfigChange({ font: "customFont" });
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      };
                      input.click();
                    },
                    children: [
                      /* @__PURE__ */ jsx13(Upload2, { className: "w-3 h-3 mr-1" }),
                      texts.uploadFont
                    ]
                  }
                ),
                /* @__PURE__ */ jsx13(Button, { asChild: true, size: "sm", className: "w-full h-10 lg:h-8 mt-3 bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground", children: /* @__PURE__ */ jsxs8("a", { href: "/font-creator/", children: [
                  /* @__PURE__ */ jsx13(ALargeSmall, { className: "w-3 h-3 mr-1" }),
                  texts.createFont
                ] }) })
              ] }),
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getFontSize(textConfig.fontSize) }),
                /* @__PURE__ */ jsx13(
                  Slider,
                  {
                    value: [textConfig.fontSize],
                    onValueChange: ([value]) => handleTextConfigChange({ fontSize: value }),
                    min: limits.fontSize.min,
                    max: limits.fontSize.max,
                    step: limits.fontSize.step,
                    className: "w-full"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getLineHeight(textConfig.lineHeight) }),
                /* @__PURE__ */ jsx13(
                  Slider,
                  {
                    value: [textConfig.lineHeight],
                    onValueChange: ([value]) => handleTextConfigChange({ lineHeight: value }),
                    min: limits.lineHeight.min,
                    max: limits.lineHeight.max,
                    step: limits.lineHeight.step,
                    className: "w-full"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getLetterSpacing(textConfig.letterSpacing) }),
                /* @__PURE__ */ jsx13(
                  Slider,
                  {
                    value: [textConfig.letterSpacing],
                    onValueChange: ([value]) => handleTextConfigChange({ letterSpacing: value }),
                    min: limits.letterSpacing.min,
                    max: limits.letterSpacing.max,
                    step: limits.letterSpacing.step,
                    className: "w-full"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium", children: texts.textColor }),
                /* @__PURE__ */ jsx13("div", { className: "grid grid-cols-3 gap-1 mt-1", children: texts.colors.map((color) => /* @__PURE__ */ jsxs8(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    onClick: () => handleTextConfigChange({ color: color.value }),
                    className: "h-6 p-1 relative",
                    children: [
                      /* @__PURE__ */ jsx13(
                        "div",
                        {
                          className: "w-full h-full rounded",
                          style: { backgroundColor: color.value }
                        }
                      ),
                      textConfig.color === color.value && /* @__PURE__ */ jsx13(Check4, { className: "absolute inset-0 m-auto w-3 h-3 text-white drop-shadow-sm" })
                    ]
                  },
                  color.value
                )) }),
                /* @__PURE__ */ jsx13("div", { className: "mt-2", children: /* @__PURE__ */ jsx13(
                  "input",
                  {
                    type: "color",
                    value: textConfig.color,
                    onChange: (e) => handleTextConfigChange({ color: e.target.value }),
                    className: "w-full h-6 rounded border border-border cursor-pointer"
                  }
                ) })
              ] })
            ] })
          ] }) })
        ] }),
        /* @__PURE__ */ jsxs8(Popover, { children: [
          /* @__PURE__ */ jsx13(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs8(Button, { variant: "outline", size: "sm", className: "h-10 lg:h-8 gap-1 px-3 lg:px-2", children: [
            /* @__PURE__ */ jsx13(FileText2, { className: "w-5 h-5 lg:w-4 lg:h-4" }),
            /* @__PURE__ */ jsx13("span", { className: "hidden lg:inline", children: texts.bgSettings }),
            /* @__PURE__ */ jsx13(ChevronDown3, { className: "w-4 h-4 lg:w-3 lg:h-3" })
          ] }) }),
          /* @__PURE__ */ jsx13(PopoverContent, { className: "w-[90vw] max-w-lg lg:w-80 z-50 bg-white", children: /* @__PURE__ */ jsxs8("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx13("h4", { className: "font-medium", children: texts.bgSettings }),
              /* @__PURE__ */ jsxs8(
                Button,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onResetBackgroundSettings,
                  className: "h-6 text-xs text-destructive hover:text-accent-foreground",
                  disabled: !onResetBackgroundSettings,
                  children: [
                    /* @__PURE__ */ jsx13(RotateCcw2, { className: "w-3 h-3" }),
                    texts.reset
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs8("div", { className: "space-y-3", children: [
              showNewFeatures && /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
                /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsxs8(Label, { className: "text-sm font-medium flex items-center gap-2", children: [
                    texts.doublePage,
                    (!isAuthenticated || !hasDoublePage) && /* @__PURE__ */ jsx13(Lock2, { size: 12, className: "text-gray-500" })
                  ] }),
                  /* @__PURE__ */ jsx13("span", { className: "bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium", children: "NEW" })
                ] }),
                /* @__PURE__ */ jsx13(
                  Switch,
                  {
                    checked: backgroundConfig.doublePage,
                    onCheckedChange: handleDoublePageChange
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium", children: texts.bgType }),
                /* @__PURE__ */ jsx13("div", { className: "grid grid-cols-3 gap-2", children: (isCustomBackgroundLoaded ? ["grid", "lines", "blank", "custom"] : ["grid", "lines", "blank"]).map((bgType) => /* @__PURE__ */ jsx13("div", { className: "text-center", children: /* @__PURE__ */ jsx13(
                  Button,
                  {
                    variant: backgroundConfig.backgroundType === bgType ? "default" : "outline",
                    size: "sm",
                    onClick: () => handleBackgroundConfigChange({ backgroundType: bgType }),
                    className: "w-full h-16 p-1 flex flex-col gap-1",
                    children: /* @__PURE__ */ jsx13("div", { className: "flex-1 w-full bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center", children: /* @__PURE__ */ jsx13("span", { className: "text-xs text-gray-500", children: bgType === "grid" ? texts.bgTypes.grid : bgType === "lines" ? texts.bgTypes.lines : bgType === "blank" ? texts.bgTypes.blank : texts.bgTypes.custom }) })
                  }
                ) }, bgType)) }),
                /* @__PURE__ */ jsx13("div", { className: "text-center", children: /* @__PURE__ */ jsx13("span", { className: "text-xs text-muted-foreground", children: texts.or }) }),
                /* @__PURE__ */ jsxs8(
                  Button,
                  {
                    variant: "outline",
                    size: "sm",
                    className: "w-full h-10 lg:h-8 mt-1",
                    onClick: () => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".jpg,.jpeg,.png,.gif,.bmp,.webp";
                      input.onchange = (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const backgroundData = reader.result;
                            const loaded = loadBackgroundFromData(backgroundData);
                            if (loaded) {
                              saveCustomBackgroundToStorage(backgroundData);
                              setIsCustomBackgroundLoaded(true);
                              onCustomBackgroundLoad?.("customBackground");
                              handleBackgroundConfigChange({ backgroundType: "custom" });
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      };
                      input.click();
                    },
                    children: [
                      /* @__PURE__ */ jsx13(Upload2, { className: "w-3 h-3 mr-1" }),
                      texts.uploadBg
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsx13("div", { children: /* @__PURE__ */ jsxs8("div", { className: "space-y-3 mt-2", children: [
                /* @__PURE__ */ jsxs8("div", { children: [
                  /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getMarginLeft(textConfig.leftMargin) }),
                  /* @__PURE__ */ jsx13(
                    Slider,
                    {
                      value: [textConfig.leftMargin],
                      onValueChange: ([value]) => handleTextConfigChange({ leftMargin: value }),
                      min: limits.leftMargin.min,
                      max: limits.leftMargin.max,
                      step: limits.leftMargin.step,
                      className: "w-full"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs8("div", { children: [
                  /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getMarginRight(textConfig.rightMargin) }),
                  /* @__PURE__ */ jsx13(
                    Slider,
                    {
                      value: [textConfig.rightMargin],
                      onValueChange: ([value]) => handleTextConfigChange({ rightMargin: value }),
                      min: limits.rightMargin.min,
                      max: limits.rightMargin.max,
                      step: limits.rightMargin.step,
                      className: "w-full"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs8("div", { children: [
                  /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getMarginTop(textConfig.topMargin) }),
                  /* @__PURE__ */ jsx13(
                    Slider,
                    {
                      value: [textConfig.topMargin],
                      onValueChange: ([value]) => handleTextConfigChange({ topMargin: value }),
                      min: limits.topMargin.min,
                      max: limits.topMargin.max,
                      step: limits.topMargin.step,
                      className: "w-full"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs8("div", { children: [
                  /* @__PURE__ */ jsx13(Label, { className: "text-sm font-medium mb-2 block", children: texts.getRedLine(textConfig.indentSize) }),
                  /* @__PURE__ */ jsx13(
                    Slider,
                    {
                      value: [textConfig.indentSize],
                      onValueChange: ([value]) => handleTextConfigChange({ indentSize: value }),
                      min: limits.indentSize.min,
                      max: limits.indentSize.max,
                      step: limits.indentSize.step,
                      className: "w-full"
                    }
                  )
                ] })
              ] }) })
            ] })
          ] }) })
        ] })
      ] }),
      /* @__PURE__ */ jsx13("div", { className: "flex-1" }),
      /* @__PURE__ */ jsx13("div", { className: "flex items-center", children: /* @__PURE__ */ jsx13(Button, { asChild: true, size: "sm", variant: "outline", className: "h-10 lg:h-8 text-foreground", children: /* @__PURE__ */ jsxs8("a", { href: "/font-creator/", children: [
        /* @__PURE__ */ jsx13(ALargeSmall, { className: "w-3 h-3 mr-1" }),
        texts.createFont
      ] }) }) })
    ] }) }),
    topBanner && /* @__PURE__ */ jsx13("div", { className: "lg:hidden order-1", children: topBanner }),
    /* @__PURE__ */ jsxs8("div", { className: "flex flex-col lg:flex-row", children: [
      /* @__PURE__ */ jsx13("div", { className: "flex-1 bg-gray-50 order-2 lg:order-1", children: /* @__PURE__ */ jsxs8("div", { className: "flex justify-center gap-4 lg:gap-8 p-4 lg:p-8", children: [
        leftBanner && /* @__PURE__ */ jsx13("div", { className: "hidden lg:block w-[160px] flex-shrink-0 sticky top-[128px] self-start", children: leftBanner }),
        /* @__PURE__ */ jsx13("div", { className: "flex-1 max-w-4xl", children: /* @__PURE__ */ jsx13(
          PreviewArea,
          {
            text: inputText,
            textConfig,
            backgroundConfig,
            PreviewComponent,
            texts: texts.previewArea
          }
        ) }),
        rightBanner && /* @__PURE__ */ jsx13("div", { className: "hidden lg:block w-[160px] flex-shrink-0 sticky top-[128px] self-start", children: rightBanner })
      ] }) }),
      /* @__PURE__ */ jsx13("div", { className: "w-full lg:min-w-[320px] lg:w-[320px] flex flex-col order-1 lg:order-2 lg:sticky lg:top-[108px] lg:h-[calc(100vh-110px)]", children: /* @__PURE__ */ jsxs8("div", { className: "flex flex-col h-full", children: [
        !!showAIFeatures && /* @__PURE__ */ jsx13("div", { className: "p-4 pb-0 bg-white border-t lg:border-t-0 lg:border-l border-border flex-shrink-0", children: /* @__PURE__ */ jsxs8("div", { className: "grid w-full grid-cols-2 h-10 p-1 bg-muted rounded-md", children: [
          /* @__PURE__ */ jsx13(
            "button",
            {
              onClick: () => setActiveTab("text"),
              className: cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
                activeTab === "text" ? "bg-background text-foreground shadow-sm" : ""
              ),
              children: texts.notesTab
            }
          ),
          /* @__PURE__ */ jsxs8(
            "button",
            {
              onClick: () => {
                if (window.innerWidth < 1024) {
                  setIsAiChatFullscreen(true);
                } else {
                  setActiveTab("ai");
                }
              },
              className: cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all relative",
                activeTab === "ai" ? "bg-background text-foreground shadow-sm" : ""
              ),
              children: [
                /* @__PURE__ */ jsxs8("span", { className: "flex items-center gap-1", children: [
                  texts.aiTab,
                  (!isAuthenticated || aiTokens === 0) && /* @__PURE__ */ jsx13(Lock2, { size: 12, className: "text-gray-500" })
                ] }),
                /* @__PURE__ */ jsx13("span", { className: "absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 flex-shrink-0", children: "NEW" })
              ]
            }
          )
        ] }) }),
        activeTab === "text" && /* @__PURE__ */ jsxs8("div", { className: "flex flex-col flex-1 bg-white lg:border-l border-border", children: [
          /* @__PURE__ */ jsxs8("div", { className: "p-4 border-b border-border flex-shrink-0", children: [
            /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between mb-2", children: [
              /* @__PURE__ */ jsx13("h3", { className: "font-medium", children: texts.notesText }),
              /* @__PURE__ */ jsxs8("span", { className: "text-xs text-muted-foreground", children: [
                inputText.length,
                " ",
                texts.characters
              ] })
            ] }),
            /* @__PURE__ */ jsxs8("div", { className: "flex gap-2", children: [
              /* @__PURE__ */ jsxs8(Popover, { open: showRecognitionModal, onOpenChange: setShowRecognitionModal, children: [
                /* @__PURE__ */ jsx13(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs8(Button, { size: "sm", className: "flex-1 h-10 lg:h-8 bg-gradient-accent hover:bg-accent shadow-accent relative", children: [
                  recognitionState.isProcessing ? /* @__PURE__ */ jsx13("div", { className: "w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" }) : /* @__PURE__ */ jsx13(Upload2, { className: "w-4 h-4 mr-2" }),
                  recognitionState.isProcessing ? "\u0420\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0451\u043C..." : texts.recognizeText,
                  !recognitionState.isProcessing && /* @__PURE__ */ jsx13("span", { className: "absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none", children: "NEW" })
                ] }) }),
                /* @__PURE__ */ jsx13(PopoverContent, { className: "w-[90vw] max-w-lg lg:w-[30rem] z-50 bg-white", children: /* @__PURE__ */ jsx13(
                  TextRecognition,
                  {
                    state: recognitionState,
                    onChange: (patch) => setRecognitionState((prev) => ({ ...prev, ...patch })),
                    onTextExtracted: (text) => {
                      handleTextChange(text);
                      setRecognitionState(defaultRecognitionState);
                      setShowRecognitionModal(false);
                    },
                    onFileUpload: onTextRecognition,
                    onDocumentUpload: onDocumentRecognition,
                    onYoutubeRecognize: onYoutubeRecognition,
                    onAudioRecognize: onAudioRecognition,
                    isLocked: !isAuthenticated || aiTokens === 0,
                    onLocked: () => {
                      setShowRecognitionModal(false);
                      if (!isAuthenticated) {
                        onShowAuthModal?.();
                      } else {
                        onShowSubscriptionModal?.("tokens");
                      }
                    },
                    texts: texts.textRecognition
                  }
                ) })
              ] }),
              materials.length > 0 && onLoadMaterial && /* @__PURE__ */ jsxs8(Popover, { children: [
                /* @__PURE__ */ jsx13(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs8(Button, { variant: "outline", size: "sm", className: "h-10 lg:h-8 px-3 relative", title: texts.textRecognition.myMaterials, children: [
                  /* @__PURE__ */ jsx13(BookOpen, { className: "w-4 h-4" }),
                  /* @__PURE__ */ jsx13("span", { className: "absolute -top-1 -right-1 w-4 h-4 bg-accent text-white text-[10px] rounded-full flex items-center justify-center font-medium", children: materials.length > 9 ? "9+" : materials.length })
                ] }) }),
                /* @__PURE__ */ jsxs8(PopoverContent, { className: "w-72 z-50 bg-white p-3", align: "end", children: [
                  /* @__PURE__ */ jsx13("p", { className: "text-sm font-semibold text-foreground mb-0.5", children: texts.textRecognition.myMaterials }),
                  /* @__PURE__ */ jsx13("p", { className: "text-xs text-muted-foreground mb-2", children: texts.textRecognition.materialsHint }),
                  /* @__PURE__ */ jsx13("div", { className: "flex flex-col gap-0.5 max-h-60 overflow-y-auto", children: materials.map((m) => {
                    const isProcessingMaterial = m.status === "processing";
                    return /* @__PURE__ */ jsxs8(
                      "button",
                      {
                        onClick: () => !isProcessingMaterial && onLoadMaterial(m.id),
                        disabled: isProcessingMaterial,
                        className: `flex items-start gap-2 text-left px-2 py-2 rounded-lg transition-colors ${isProcessingMaterial ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"}`,
                        children: [
                          /* @__PURE__ */ jsx13("div", { className: "mt-0.5 flex-shrink-0", children: isProcessingMaterial ? /* @__PURE__ */ jsx13("div", { className: "w-3.5 h-3.5 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" }) : /* @__PURE__ */ jsx13(SourceIcon, { meta: m.sourceMeta }) }),
                          /* @__PURE__ */ jsxs8("div", { className: "min-w-0 flex-1", children: [
                            /* @__PURE__ */ jsx13("p", { className: "text-xs font-medium text-foreground truncate", children: m.title || "\u2014" }),
                            /* @__PURE__ */ jsxs8("p", { className: "text-[10px] text-muted-foreground mt-0.5", children: [
                              m.sourceMeta?.type === "photo" ? texts.textRecognition.sourcePhoto : m.sourceMeta?.type === "document" ? m.sourceMeta.filename ?? texts.textRecognition.sourceDocument : m.sourceMeta?.type === "youtube" ? texts.textRecognition.sourceYoutube : m.sourceMeta?.type === "audio" ? texts.textRecognition.sourceAudio : texts.textRecognition.sourceText,
                              " \xB7 ",
                              new Date(m.createdAt).toLocaleDateString(void 0, { day: "numeric", month: "short" })
                            ] })
                          ] })
                        ]
                      },
                      m.id
                    );
                  }) })
                ] })
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsx13("div", { className: "flex-1 p-4", children: /* @__PURE__ */ jsx13(
            Textarea,
            {
              placeholder: texts.textPlaceholder,
              value: inputText,
              onChange: (e) => handleTextChange(e.target.value),
              className: "w-full h-full min-h-[200px] lg:min-h-0 resize-none border-0 shadow-none focus:ring-0",
              autoFocus: true
            }
          ) }),
          /* @__PURE__ */ jsx13("div", { className: "px-4 pb-4 flex-shrink-0", children: /* @__PURE__ */ jsxs8(Popover, { children: [
            /* @__PURE__ */ jsx13(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs8(
              Button,
              {
                size: "sm",
                className: "w-full h-10 lg:h-8 bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground",
                children: [
                  /* @__PURE__ */ jsx13(Download2, { className: "w-5 h-5 lg:w-4 lg:h-4 mr-2" }),
                  texts.download
                ]
              }
            ) }),
            /* @__PURE__ */ jsx13(PopoverContent, { className: "w-[90vw] max-w-md lg:w-[23rem] z-50 bg-white", children: /* @__PURE__ */ jsxs8("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsx13("h4", { className: "font-medium", children: texts.downloadOptions }),
              /* @__PURE__ */ jsxs8("div", { className: "space-y-1", children: [
                showNewFeatures && /* @__PURE__ */ jsx13(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "w-full justify-start h-auto p-3 text-left relative group",
                    onClick: handlePDFClick,
                    children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between w-full", children: [
                      /* @__PURE__ */ jsx13("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs8("div", { children: [
                        /* @__PURE__ */ jsxs8("div", { className: "font-medium flex items-center gap-2", children: [
                          texts.downloadPDF,
                          (!isAuthenticated || !hasSubscription) && /* @__PURE__ */ jsx13(Lock2, { size: 14, className: "text-gray-500 group-hover:!text-white" })
                        ] }),
                        /* @__PURE__ */ jsx13("div", { className: "text-xs text-muted-foreground group-hover:text-white", children: texts.downloadPDFDesc })
                      ] }) }),
                      /* @__PURE__ */ jsx13("span", { className: "bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 flex-shrink-0", children: "NEW" })
                    ] })
                  }
                ),
                /* @__PURE__ */ jsx13("div", { className: "w-full relative", children: /* @__PURE__ */ jsx13(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "w-full justify-start h-auto p-3 text-left relative group",
                    onClick: onDownloadNormal,
                    disabled: !onDownloadNormal,
                    children: /* @__PURE__ */ jsx13("div", { className: "flex items-center justify-between w-full", children: /* @__PURE__ */ jsx13("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs8("div", { children: [
                      /* @__PURE__ */ jsx13("div", { className: "font-medium flex items-center gap-2", children: texts.downloadNormal }),
                      /* @__PURE__ */ jsx13("div", { className: "text-xs text-muted-foreground group-hover:text-white", children: texts.downloadNormalDesc })
                    ] }) }) })
                  }
                ) }),
                /* @__PURE__ */ jsx13("div", { className: "w-full relative", children: /* @__PURE__ */ jsx13(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "w-full justify-start h-auto p-3 text-left relative group",
                    onClick: onDownloadHighRes,
                    disabled: !onDownloadHighRes,
                    children: /* @__PURE__ */ jsx13("div", { className: "flex items-center justify-between w-full", children: /* @__PURE__ */ jsx13("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs8("div", { children: [
                      /* @__PURE__ */ jsx13("div", { className: "font-medium flex items-center gap-2", children: texts.downloadHighRes }),
                      /* @__PURE__ */ jsx13("div", { className: "text-xs text-muted-foreground group-hover:text-white", children: texts.downloadHighResDesc })
                    ] }) }) })
                  }
                ) })
              ] })
            ] }) })
          ] }) })
        ] }),
        /* @__PURE__ */ jsx13("div", { className: cn("flex flex-col flex-1 bg-white lg:border-l border-border overflow-hidden", activeTab !== "ai" && !isAiChatFullscreen ? "hidden" : ""), children: /* @__PURE__ */ jsx13(
          AiChatInterface,
          {
            messages: chatMessages,
            isProcessing: isAIProcessing,
            inputDisabled: isAIStreaming,
            streamingMessageId: aiStreamingMessageId,
            availableTokens: aiTokens,
            aiActions: aiActions.length > 0 ? aiActions : [{ id: "default", name: "\u041E\u0431\u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C \u0442\u0435\u043A\u0441\u0442" }],
            isFullscreen: isAiChatFullscreen,
            onSetFullscreen: setIsAiChatFullscreen,
            onSendMessage: (text, actionId) => {
              if (!isAuthenticated) {
                setIsAiChatFullscreen(false);
                onShowAuthModal?.();
                return;
              }
              if (aiTokens === 0) {
                setIsAiChatFullscreen(false);
                onShowSubscriptionModal?.("tokens");
                return;
              }
              return onSendChatMessage?.(text, actionId);
            },
            className: "flex-1",
            texts: texts.aiChat,
            chats,
            currentChatId,
            onSelectChat,
            onCreateChat,
            onRenameChat,
            onDeleteChat,
            isCreatingChat,
            renamingLoadingId,
            deletingChatId
          }
        ) })
      ] }) })
    ] })
  ] });
}

// src/components/LoadingOverlay.tsx
import { Loader2 as Loader22 } from "lucide-react";
import { jsx as jsx14, jsxs as jsxs9 } from "react/jsx-runtime";
function LoadingOverlay({ isVisible, message = "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430..." }) {
  if (!isVisible) return null;
  return /* @__PURE__ */ jsx14("div", { className: "fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center", children: /* @__PURE__ */ jsxs9("div", { className: "bg-white rounded-lg shadow-xl p-8 flex flex-col items-center space-y-4", children: [
    /* @__PURE__ */ jsx14(Loader22, { className: "w-8 h-8 animate-spin text-primary" }),
    /* @__PURE__ */ jsx14("p", { className: "text-lg font-medium text-foreground", children: message })
  ] }) });
}

// src/components/ui/card.tsx
import * as React13 from "react";
import { jsx as jsx15 } from "react/jsx-runtime";
var Card = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  "h3",
  {
    ref,
    className: cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/components/PlanCard.tsx
import { Check as Check5 } from "lucide-react";
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var ORDERED_FEATURES = ["recognition_photo", "recognition_document", "recognition_youtube", "recognition_audio", "double_page", "pdf_export", "no_ads"];
function PlanCard({
  plan,
  premiumPlan,
  isLoading,
  featureLabels,
  hasPremium,
  isPopular = false,
  onPlanAction,
  priceDisplay,
  priceKzt,
  previewMode = false,
  texts
}) {
  const t = texts;
  const isTokenPlan = plan.tokens && plan.tokens > 0;
  const isPremiumPlan = plan.features.includes("pdf_export");
  const totalPrice = plan.price;
  const handleClick = () => {
    onPlanAction(plan.id, false, totalPrice);
  };
  const ownFeatures = ORDERED_FEATURES.filter((f) => plan.features.includes(f));
  return /* @__PURE__ */ jsxs10(Card, { className: `relative flex flex-col hover:shadow-lg transition-shadow ${isPopular ? "ring-2 ring-blue-500 scale-105" : ""}`, children: [
    !!isPopular && /* @__PURE__ */ jsx16("div", { className: "absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium", children: t.popular }),
    /* @__PURE__ */ jsxs10(CardHeader, { className: "text-center", children: [
      /* @__PURE__ */ jsx16(CardTitle, { className: "text-lg", children: plan.name }),
      /* @__PURE__ */ jsx16("div", { className: "text-sm text-muted-foreground", children: t.forDuration(t.getDuration(plan.durationDays)) })
    ] }),
    /* @__PURE__ */ jsxs10(CardContent, { className: "flex flex-col flex-1 space-y-4", children: [
      /* @__PURE__ */ jsxs10("div", { className: "space-y-2 flex-1", children: [
        /* @__PURE__ */ jsx16("p", { className: "text-xs font-medium uppercase tracking-wide text-muted-foreground", children: t.whatsIncluded }),
        /* @__PURE__ */ jsxs10("ul", { className: "space-y-1.5", children: [
          !!isTokenPlan && /* @__PURE__ */ jsxs10("li", { className: "flex items-start gap-2 text-sm", children: [
            /* @__PURE__ */ jsx16(Check5, { className: "h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" }),
            /* @__PURE__ */ jsx16("span", { className: "font-medium", children: t.getTokenDescription(plan.tokens / 1e3) })
          ] }),
          ownFeatures.map((feature) => /* @__PURE__ */ jsxs10("li", { className: "flex items-center gap-2 text-sm", children: [
            /* @__PURE__ */ jsx16(Check5, { className: "h-4 w-4 text-green-500 flex-shrink-0" }),
            featureLabels[feature] || feature
          ] }, feature))
        ] })
      ] }),
      priceKzt ? /* @__PURE__ */ jsxs10("div", { className: "flex items-center justify-center", children: [
        /* @__PURE__ */ jsxs10("div", { className: "flex flex-col items-center px-4", children: [
          /* @__PURE__ */ jsx16("div", { className: "text-lg font-bold", children: priceDisplay ?? `${totalPrice}\u20BD` }),
          /* @__PURE__ */ jsx16("div", { className: "text-xs text-muted-foreground mt-0.5", children: t.russia ?? "\u0420\u043E\u0441\u0441\u0438\u044F" })
        ] }),
        /* @__PURE__ */ jsx16("div", { className: "w-px h-10 bg-gray-200" }),
        /* @__PURE__ */ jsxs10("div", { className: "flex flex-col items-center px-4", children: [
          /* @__PURE__ */ jsxs10("div", { className: "text-lg font-bold", children: [
            priceKzt,
            " \u20B8"
          ] }),
          /* @__PURE__ */ jsx16("div", { className: "text-xs text-muted-foreground mt-0.5", children: t.world ?? "\u0412\u0435\u0441\u044C \u043C\u0438\u0440" })
        ] })
      ] }) : /* @__PURE__ */ jsx16("div", { className: "text-center", children: /* @__PURE__ */ jsx16("div", { className: "text-lg font-bold", children: priceDisplay ?? `${totalPrice}\u20BD` }) }),
      previewMode ? /* @__PURE__ */ jsx16(Button, { disabled: true, className: "w-full", variant: "outline", size: "lg", children: t.comingSoon }) : hasPremium && isPremiumPlan ? /* @__PURE__ */ jsx16(Button, { disabled: true, className: "w-full", variant: "outline", size: "lg", children: t.purchased ?? "\u2713" }) : /* @__PURE__ */ jsx16(
        Button,
        {
          onClick: handleClick,
          disabled: isLoading,
          className: `w-full ${isPopular ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" : isPremiumPlan ? "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600" : ""}`,
          variant: isPopular || isPremiumPlan ? "default" : "outline",
          size: "lg",
          children: isLoading ? t.processing : t.buy
        }
      )
    ] })
  ] });
}

// src/components/SubscriptionModal.tsx
import { Loader2 as Loader23 } from "lucide-react";
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
function SubscriptionModal({
  isOpen,
  onClose,
  onSubscribe,
  onTokenPurchase,
  plans = [],
  loadingPlanId = null,
  featureLabels = {},
  hasPremium = false,
  isLoadingPlans = false,
  texts,
  planCardTexts
}) {
  const premiumPlan = plans.find((p) => p.features.includes("pdf_export"));
  const tokenPlans = plans.filter((p) => p.tokens && p.tokens > 0);
  const allDisplayPlans = [...!hasPremium && premiumPlan ? [premiumPlan] : [], ...tokenPlans];
  const handlePlanAction = (planId, includePremium, totalPrice) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan?.tokens && plan.tokens > 0) {
      onTokenPurchase?.(planId, includePremium, totalPrice);
    } else {
      onSubscribe?.(planId);
    }
  };
  return /* @__PURE__ */ jsx17(Dialog, { open: isOpen, onOpenChange: onClose, children: /* @__PURE__ */ jsxs11(DialogContent, { className: "w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto", children: [
    /* @__PURE__ */ jsx17(DialogHeader, { children: /* @__PURE__ */ jsx17(DialogTitle, { className: "text-2xl", children: texts?.title }) }),
    /* @__PURE__ */ jsx17("div", { className: "space-y-6 py-2", children: /* @__PURE__ */ jsxs11("div", { children: [
      /* @__PURE__ */ jsx17("p", { className: "text-sm font-medium mb-3", children: texts?.choosePlan }),
      isLoadingPlans ? /* @__PURE__ */ jsx17("div", { className: "flex items-center justify-center py-16", children: /* @__PURE__ */ jsx17(Loader23, { className: "w-6 h-6 animate-spin text-muted-foreground" }) }) : /* @__PURE__ */ jsx17("div", { className: `grid gap-4 ${allDisplayPlans.length >= 4 ? "grid-cols-1 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`, children: allDisplayPlans.map((plan) => /* @__PURE__ */ jsx17(
        PlanCard,
        {
          plan,
          premiumPlan,
          isLoading: loadingPlanId === plan.id,
          featureLabels,
          hasPremium,
          isPopular: plan.isPopular ?? plan.name === "\u0421\u0442\u0430\u043D\u0434\u0430\u0440\u0442",
          onPlanAction: handlePlanAction,
          priceDisplay: plan.priceDisplay,
          priceKzt: plan.priceKzt,
          texts: planCardTexts
        },
        plan.id
      )) })
    ] }) })
  ] }) });
}

// src/components/PaymentRegionModal.tsx
import RU from "country-flag-icons/react/3x2/RU";
import { Globe } from "lucide-react";
import { jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
function PaymentRegionModal({
  isOpen,
  onClose,
  onSelectRussia,
  onSelectWorld,
  planName,
  priceRub,
  priceKzt,
  texts
}) {
  return /* @__PURE__ */ jsx18(Dialog, { open: isOpen, onOpenChange: onClose, children: /* @__PURE__ */ jsxs12(DialogContent, { className: "max-w-md", children: [
    /* @__PURE__ */ jsxs12(DialogHeader, { children: [
      /* @__PURE__ */ jsx18(DialogTitle, { children: texts.title }),
      planName && /* @__PURE__ */ jsx18("p", { className: "text-sm text-muted-foreground", children: planName })
    ] }),
    /* @__PURE__ */ jsxs12("div", { className: "grid grid-cols-2 gap-4 mt-2", children: [
      /* @__PURE__ */ jsxs12(
        "button",
        {
          onClick: onSelectRussia,
          className: "flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer",
          children: [
            /* @__PURE__ */ jsx18("div", { className: "w-12 h-8 rounded overflow-hidden shadow-sm", children: /* @__PURE__ */ jsx18(RU, {}) }),
            /* @__PURE__ */ jsxs12("div", { className: "text-center", children: [
              /* @__PURE__ */ jsx18("p", { className: "font-semibold text-sm", children: texts.russiaTitle }),
              /* @__PURE__ */ jsx18("p", { className: "text-xs text-muted-foreground mt-0.5", children: texts.russiaSubtitle })
            ] }),
            /* @__PURE__ */ jsxs12("p", { className: "text-xl font-bold", children: [
              priceRub,
              "\u20BD"
            ] })
          ]
        }
      ),
      /* @__PURE__ */ jsxs12(
        "button",
        {
          disabled: true,
          className: "relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed",
          children: [
            /* @__PURE__ */ jsx18("span", { className: "absolute -top-2.5 -right-2.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-400 text-white uppercase", children: texts.comingSoon }),
            /* @__PURE__ */ jsx18(Globe, { className: "w-12 h-8 text-gray-400" }),
            /* @__PURE__ */ jsxs12("div", { className: "text-center", children: [
              /* @__PURE__ */ jsx18("p", { className: "font-semibold text-sm", children: texts.worldTitle }),
              /* @__PURE__ */ jsx18("p", { className: "text-xs text-muted-foreground mt-0.5", children: texts.worldSubtitle })
            ] }),
            priceKzt && /* @__PURE__ */ jsxs12("p", { className: "text-xl font-bold", children: [
              priceKzt,
              " \u20B8"
            ] })
          ]
        }
      )
    ] })
  ] }) });
}

// src/components/ProfilePage.tsx
import { useEffect as useEffect3 } from "react";

// src/components/ui/avatar.tsx
import * as React14 from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { jsx as jsx19 } from "react/jsx-runtime";
var Avatar = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    ),
    ...props
  }
));
Avatar.displayName = AvatarPrimitive.Root.displayName;
var AvatarImage = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
  AvatarPrimitive.Image,
  {
    ref,
    className: cn("aspect-square h-full w-full", className),
    ...props
  }
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// src/components/ui/badge.tsx
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx20 } from "react/jsx-runtime";
var badgeVariants = cva3(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx20("div", { className: cn(badgeVariants({ variant }), className), ...props });
}

// src/components/ProfilePage.tsx
import { User, CreditCard, Check as Check6 } from "lucide-react";
import { jsx as jsx21, jsxs as jsxs13 } from "react/jsx-runtime";
var ProfilePage = ({
  user,
  currentSubscription,
  hasSubscription,
  isLoading,
  onFetchCurrentSubscription,
  onCancelSubscription,
  onSubscribe,
  texts,
  tokensRemaining = 0,
  tokensPurchased = 0,
  tokensPlanName,
  tokensExpireAt
}) => {
  useEffect3(() => {
    onFetchCurrentSubscription();
  }, [onFetchCurrentSubscription]);
  const handleCancelSubscription = async () => {
    if (currentSubscription?.id && window.confirm(texts?.confirmCancel)) {
      try {
        await onCancelSubscription(currentSubscription.id);
      } catch (error) {
        console.error("Error cancelling subscription:", error);
      }
    }
  };
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(texts?.dateLocale);
  };
  const getFeatureLabel = (feature) => {
    return texts?.featureLabels?.[feature] || feature;
  };
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(" ").map((n) => n.charAt(0)).join("").substring(0, 2).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "?";
  };
  if (!user) {
    return /* @__PURE__ */ jsx21("div", { className: "container mx-auto px-4 py-8 max-w-4xl", children: /* @__PURE__ */ jsx21(Card, { children: /* @__PURE__ */ jsxs13(CardContent, { className: "p-8 text-center", children: [
      /* @__PURE__ */ jsx21("h1", { className: "text-2xl font-bold mb-4", children: texts?.accessDenied }),
      /* @__PURE__ */ jsx21("p", { className: "text-muted-foreground", children: texts?.accessDeniedMessage })
    ] }) }) });
  }
  return /* @__PURE__ */ jsxs13("div", { className: "container mx-auto px-4 py-8 max-w-4xl space-y-6", children: [
    /* @__PURE__ */ jsxs13("div", { className: "flex items-center gap-4 mb-8", children: [
      /* @__PURE__ */ jsx21(Avatar, { className: "h-16 w-16", children: /* @__PURE__ */ jsx21(AvatarFallback, { className: "bg-gradient-primary text-primary-foreground text-xl font-bold", children: getUserInitials() }) }),
      /* @__PURE__ */ jsxs13("div", { children: [
        /* @__PURE__ */ jsx21("h1", { className: "text-3xl font-bold", children: user.name || texts?.defaultUserName }),
        /* @__PURE__ */ jsx21("p", { className: "text-muted-foreground", children: user.email })
      ] })
    ] }),
    /* @__PURE__ */ jsxs13(Card, { children: [
      /* @__PURE__ */ jsx21(CardHeader, { children: /* @__PURE__ */ jsxs13(CardTitle, { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx21(User, { className: "h-5 w-5" }),
        texts?.profileSection
      ] }) }),
      /* @__PURE__ */ jsx21(CardContent, { children: /* @__PURE__ */ jsxs13("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsxs13("div", { className: "flex", children: [
          /* @__PURE__ */ jsx21("span", { className: "text-muted-foreground", children: texts?.nameLabel }),
          /* @__PURE__ */ jsx21("span", { className: "font-medium", children: user.name || texts?.notSet })
        ] }),
        /* @__PURE__ */ jsxs13("div", { className: "flex", children: [
          /* @__PURE__ */ jsx21("span", { className: "text-muted-foreground", children: texts?.emailLabel }),
          /* @__PURE__ */ jsx21("span", { className: "font-medium", children: user.email })
        ] })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs13(Card, { children: [
      /* @__PURE__ */ jsxs13(CardHeader, { children: [
        /* @__PURE__ */ jsxs13(CardTitle, { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx21(CreditCard, { className: "h-5 w-5" }),
          texts?.subscriptionSection
        ] }),
        /* @__PURE__ */ jsx21(CardDescription, { children: texts?.subscriptionDescription })
      ] }),
      /* @__PURE__ */ jsxs13(CardContent, { children: [
        isLoading ? /* @__PURE__ */ jsx21("div", { className: "flex items-center justify-center py-8", children: /* @__PURE__ */ jsx21("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary" }) }) : hasSubscription && currentSubscription ? /* @__PURE__ */ jsx21("div", { className: "space-y-4", children: /* @__PURE__ */ jsx21("div", { className: "flex items-center justify-between p-4 border rounded-lg", children: /* @__PURE__ */ jsxs13("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs13("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx21("h3", { className: "font-semibold text-lg", children: currentSubscription.plan?.name }),
            /* @__PURE__ */ jsx21(Badge, { variant: currentSubscription.status === "active" ? "default" : "secondary", children: currentSubscription.status === "active" ? texts?.activeStatus : texts?.inactiveStatus })
          ] }),
          currentSubscription.plan?.features && /* @__PURE__ */ jsxs13("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx21("p", { className: "text-sm font-medium", children: texts?.includedFeatures }),
            /* @__PURE__ */ jsx21("ul", { className: "space-y-1", children: currentSubscription.plan.features.map((feature, index) => /* @__PURE__ */ jsxs13("li", { className: "flex items-center gap-2 text-sm", children: [
              /* @__PURE__ */ jsx21(Check6, { className: "h-4 w-4 text-green-500 flex-shrink-0" }),
              getFeatureLabel(feature)
            ] }, index)) })
          ] })
        ] }) }) }) : tokensRemaining <= 0 ? /* @__PURE__ */ jsxs13("div", { className: "text-center py-8 space-y-4", children: [
          /* @__PURE__ */ jsxs13("div", { className: "text-muted-foreground", children: [
            /* @__PURE__ */ jsx21(CreditCard, { className: "h-12 w-12 mx-auto mb-4 opacity-50" }),
            /* @__PURE__ */ jsx21("p", { className: "text-lg", children: texts?.noSubscription }),
            /* @__PURE__ */ jsx21("p", { className: "text-sm", children: texts?.noSubscriptionHint })
          ] }),
          /* @__PURE__ */ jsx21(Button, { onClick: onSubscribe, children: texts?.getSubscriptionLink })
        ] }) : null,
        tokensRemaining > 0 && /* @__PURE__ */ jsx21("div", { className: "flex items-center justify-between p-4 border rounded-lg mt-4", children: /* @__PURE__ */ jsxs13("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs13("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx21("h3", { className: "font-semibold text-lg", children: tokensPlanName || texts?.tokensSection }),
            /* @__PURE__ */ jsx21(Badge, { variant: "default", children: texts?.activeStatus })
          ] }),
          /* @__PURE__ */ jsx21("div", { className: "space-y-1", children: /* @__PURE__ */ jsxs13("ul", { className: "space-y-1", children: [
            tokensPurchased > 0 && /* @__PURE__ */ jsxs13("li", { className: "flex items-center gap-2 text-sm", children: [
              /* @__PURE__ */ jsx21(Check6, { className: "h-4 w-4 text-green-500 flex-shrink-0" }),
              tokensPurchased.toLocaleString(),
              " ",
              texts?.tokensPurchasedLabel
            ] }),
            tokensExpireAt && /* @__PURE__ */ jsxs13("li", { className: "text-sm text-muted-foreground ml-6", children: [
              texts?.tokensExpire,
              ": ",
              formatDate(tokensExpireAt)
            ] })
          ] }) })
        ] }) })
      ] })
    ] })
  ] });
};

// src/components/UserAvatar.tsx
import { jsx as jsx22, jsxs as jsxs14 } from "react/jsx-runtime";
var UserAvatar = ({
  user,
  size = "sm",
  showName = false,
  onClick,
  className = "",
  defaultUserName
}) => {
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(" ").map((n) => n.charAt(0)).join("").substring(0, 2).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "?";
  };
  const getDisplayName = () => {
    return user?.name || user?.email?.split("@")[0] || (defaultUserName ?? "");
  };
  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12"
  };
  const textSizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base"
  };
  if (!user) {
    return null;
  }
  const avatarElement = /* @__PURE__ */ jsx22(Avatar, { className: `${sizeClasses[size]} ${className}`, children: /* @__PURE__ */ jsx22(AvatarFallback, { className: `bg-gradient-primary text-primary-foreground ${textSizeClasses[size]} font-bold`, children: getUserInitials() }) });
  if (onClick) {
    return /* @__PURE__ */ jsxs14(
      Button,
      {
        variant: "ghost",
        className: `p-2 h-auto gap-2 hover:bg-accent/50 ${showName ? "flex-row" : ""}`,
        onClick,
        children: [
          avatarElement,
          showName && /* @__PURE__ */ jsx22("span", { className: "text-sm font-medium", children: getDisplayName() })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs14("div", { className: `flex items-center gap-2 ${showName ? "flex-row" : ""}`, children: [
    avatarElement,
    showName && /* @__PURE__ */ jsx22("span", { className: "text-sm font-medium", children: getDisplayName() })
  ] });
};

// src/components/PricingPage.tsx
import { jsx as jsx23, jsxs as jsxs15 } from "react/jsx-runtime";
function PricingPage({
  plans,
  featureLabels,
  title,
  subtitle,
  planCardTexts
}) {
  const premiumPlan = plans.find((p) => p.features.includes("pdf_export"));
  return /* @__PURE__ */ jsx23("div", { className: "py-12 px-4", children: /* @__PURE__ */ jsxs15("div", { className: "max-w-5xl mx-auto", children: [
    /* @__PURE__ */ jsx23("h1", { className: "text-3xl font-bold text-center mb-2", children: title }),
    /* @__PURE__ */ jsx23("p", { className: "text-center text-muted-foreground mb-10", children: subtitle }),
    /* @__PURE__ */ jsx23("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6", children: plans.map((plan) => /* @__PURE__ */ jsx23(
      PlanCard,
      {
        plan,
        premiumPlan,
        isLoading: false,
        featureLabels: featureLabels ?? {},
        hasPremium: false,
        isPopular: plan.isPopular,
        onPlanAction: () => {
        },
        priceDisplay: plan.priceDisplay,
        previewMode: true,
        texts: planCardTexts
      },
      plan.id
    )) })
  ] }) });
}

// src/components/ui/tabs.tsx
import * as React16 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx24 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
var TabsList = React16.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx24(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React16.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx24(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React16.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx24(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// src/components/ui/input.tsx
import * as React17 from "react";
import { jsx as jsx25 } from "react/jsx-runtime";
var Input = React17.forwardRef(
  ({ className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx25(
      "input",
      {
        type,
        className: cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/ui/sheet.tsx
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva as cva4 } from "class-variance-authority";
import { X as X3 } from "lucide-react";
import * as React18 from "react";
import { jsx as jsx26, jsxs as jsxs16 } from "react/jsx-runtime";
var Sheet = SheetPrimitive.Root;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = SheetPrimitive.Portal;
var SheetOverlay = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  SheetPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-[70] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetVariants = cva4(
  "fixed z-[70] gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);
var SheetContent = React18.forwardRef(({ side = "right", className, children, ...props }, ref) => /* @__PURE__ */ jsxs16(SheetPortal, { children: [
  /* @__PURE__ */ jsx26(SheetOverlay, {}),
  /* @__PURE__ */ jsxs16(
    SheetPrimitive.Content,
    {
      ref,
      className: cn(sheetVariants({ side }), className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs16(SheetPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary", children: [
          /* @__PURE__ */ jsx26(X3, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx26("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
SheetContent.displayName = SheetPrimitive.Content.displayName;
var SheetHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx26(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx26(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  SheetPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold text-foreground", className),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  SheetPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// src/components/ui/tooltip.tsx
import * as React19 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx27 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React19.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx27(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-[200] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// src/components/ui/alert.tsx
import * as React20 from "react";
import { cva as cva5 } from "class-variance-authority";
import { jsx as jsx28 } from "react/jsx-runtime";
var alertVariants = cva5(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React20.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx28(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React20.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx28(
  "h5",
  {
    ref,
    className: cn("mb-1 font-medium leading-none tracking-tight", className),
    ...props
  }
));
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React20.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx28(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";
export {
  AiChatInterface,
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  HandwritingGenerator,
  Input,
  Label,
  LoadingOverlay,
  PaymentRegionModal,
  PlanCard,
  PricingPage,
  ProfilePage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SubscriptionModal,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  UserAvatar
};
